import React, { useState } from 'react';
import { Search, Loader2, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { api, errMsg } from '../lib/api';

const money = (n) => `${Number(n) < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toFixed(2)}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—');
const STATUS_COLOR = { paid: 'text-emerald-300', in_transit: 'text-amber-300', pending: 'text-amber-300', failed: 'text-red-300', canceled: 'text-red-300' };
const Section = ({ title, children, testid }) => (
  <section data-testid={testid} className="p-4 rounded-xl border border-white/10 bg-[#0A0A0F] space-y-2">
    <h3 className="text-[10px] uppercase tracking-widest text-[#A19DB0] font-bold">{title}</h3>{children}
  </section>
);

function Payouts({ stripe }) {
  if (!stripe?.account_id) return <p className="text-xs text-[#A19DB0]">No Stripe account connected.</p>;
  return (
    <>
      <p className="text-xs text-[#A19DB0]">{stripe.account_id} · available {money(stripe.available)} · pending {money(stripe.pending)} · payouts {stripe.payouts_enabled ? 'enabled' : 'DISABLED'}</p>
      {(stripe.payouts || []).map((p) => (
        <div key={p.id} data-testid={`lookup-payout-${p.id}`} className="flex items-center justify-between text-xs border-t border-white/5 pt-2">
          <div><p className="text-white font-mono">{p.id}</p><p className="text-[#A19DB0]">sent {day(p.created)} · {p.method} · {p.destination ? `${p.destination.name || p.destination.type} ••${p.destination.last4}` : ''}</p></div>
          <div className="text-right"><p className="text-white font-mono">{money(p.amount)}</p><p className={STATUS_COLOR[p.status] || 'text-white'}>{p.status.replace('_', ' ')} · arrives {day(p.arrival_date)}</p></div>
        </div>
      ))}
      {stripe.payouts_error && <p className="text-xs text-red-300">{stripe.payouts_error}</p>}
    </>
  );
}

export function SupportLookup() {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { const r = await api.get('/admin/hub/support-lookup', { params: { q: q.trim() } }); setRes(r.data); }
    catch (e) { setRes(null); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="hub-support-lookup">
      <div className="flex gap-2">
        <Input data-testid="lookup-query-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="Email, vault ID, payout (po_…), charge (ch_…) or refund (re_…)" className="input-dark h-11" />
        <button data-testid="lookup-search-btn" onClick={search} disabled={busy} className="shrink-0 px-4 rounded-lg btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Look up</button>
      </div>
      {res && (
        <>
          <Section title="Quick answers" testid="lookup-highlights">
            {res.highlights.length === 0 ? <p className="text-xs text-[#A19DB0]">Nothing unusual.</p> : res.highlights.map((h) => <p key={h} className="text-sm text-white flex gap-2"><Lightbulb className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />{h}</p>)}
          </Section>
          <Section title="User" testid="lookup-user">
            <p className="text-white font-semibold">{res.user.name} <span className="text-xs text-[#A19DB0] font-normal">· {res.user.tier} · joined {day(res.user.created_at)}</span></p>
            <p className="text-xs text-[#A19DB0]">{res.user.email}{res.user.backup_email && ` · backup ${res.user.backup_email}`}{res.user.phone_number && ` · ${res.user.phone_number}`} · refunds {res.user.refund_count}{res.user.high_refund_user && ' · FLAGGED high-refund'}</p>
          </Section>
          <Section title="Payouts (Stripe, live)" testid="lookup-payouts"><Payouts stripe={res.stripe} /></Section>
          <Section title={`Vaults · ${res.vaults.length}`} testid="lookup-vaults">
            {res.vaults.map((v) => (
              <div key={v.goal_id} className="border-t border-white/5 pt-2 text-xs" data-testid={`lookup-vault-${v.goal_id}`}>
                <p className="text-white font-semibold">{v.name} <span className="text-[#A19DB0] font-normal">· {v.vault_type} · {v.status} · {money(v.current_amount)} of {money(v.target_amount)} · unlocks {day(v.unlock_date)}</span></p>
                {v.activity.slice(0, 4).map((a, i) => <p key={i} className="text-[#A19DB0]">{day(a.created_at)} · {a.type} · {money(a.amount)}{a.note ? ` · ${a.note}` : ''}</p>)}
                {v.charges?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {v.charges.map((c) => <a key={c.charge_id} href={`/admin/refund?charge=${c.charge_id}`} data-testid={`lookup-refund-${c.charge_id}`} className="px-2.5 py-1 rounded-full bg-[#9333EA]/20 border border-[#9333EA]/40 text-[#C8A6FF] text-[11px] font-mono hover:bg-[#9333EA]/30">Refund {money(c.amount)} · {day(c.created_at)}</a>)}
                  </div>
                )}
              </div>
            ))}
          </Section>
          {res.refunds.length > 0 && (
            <Section title="Refunds" testid="lookup-refunds">
              {res.refunds.map((r) => <p key={r.refund_id} className="text-xs text-[#A19DB0]"><span className="font-mono text-white">{r.refund_id}</span> · {money(r.amount)} · {r.status} · lands by {day(r.eta)}{r.missing_reported && ' · REPORTED MISSING'}</p>)}
            </Section>
          )}
        </>
      )}
    </div>
  );
}
